/**
 * Which graphics backend this install runs on, decided **before** a renderer
 * exists — and remembered when a driver has already proved it cannot start one.
 *
 * ## Why this supersedes half of AD-005
 *
 * AD-005 said "`WebGPURenderer` is the sole render path, no WebGL fallback",
 * and the half of it that was right is still enforced by
 * `webgpu-support.ts`'s `disableWebGlFallback`: three's own constructor wires
 * up a `getFallback` that swaps in a WebGL2 backend *silently*, so a machine
 * with a broken adapter would have shipped a quietly different picture and
 * nobody would have known which one they were looking at. That is the thing
 * worth forbidding, and it still is.
 *
 * The half that was wrong is what happened next: the routes asked
 * `hasWebGpu()`, and anyone whose answer was `false` — or whose adapter
 * request failed, or whose device request rejected — got a paragraph of prose
 * instead of a game. This module keeps the silence forbidden and drops the
 * refusal: the fallback still happens, it is **named** (here, in the boot
 * report, and on PREFERENCES), and the parity limitations it carries are
 * measured in `docs/engineering/webgl-fallback.md` rather than assumed away.
 *
 * ## What is remembered, and what deliberately is not
 *
 * Only a **driver failure** is persisted — an adapter request that produced
 * nothing, a device request that rejected, an `init()` that threw, or a
 * `GPUDevice` lost mid-sortie. Those are expensive to rediscover: the probe
 * costs a device creation and, on the drivers that fail this way, seconds of
 * a black screen before it does.
 *
 * `no-webgpu` — `navigator.gpu` simply absent — is **not** persisted, and the
 * distinction is the whole reason `RendererFallbackRecord` carries a reason at
 * all. Re-detecting an absent `navigator.gpu` is one property read on the next
 * boot, it is always right, and it flips the moment the user updates a browser
 * or takes the page out of an insecure context. Remembering it would pin a
 * WebGL picture on a machine that had grown a WebGPU one.
 *
 * ## The escape hatch, and why it is the query string
 *
 * `?renderer=webgpu` clears the stored record and re-probes. It was chosen
 * over a PREFERENCES toggle because the stored flag is not a *preference* — it
 * is a cached measurement of the machine, and a checkbox that says "use
 * WebGPU" would be a promise the driver may refuse to keep. A query string is
 * the same standing this repo already gives `?renderer=webgl` and
 * `?treatment=measured`: an explicit pin, typed deliberately, that nothing
 * links to. PREFERENCES prints the resulting fact read-only and names the
 * hatch in its footer when there is a fallback to explain.
 *
 * Nothing here imports three.js or touches the DOM, so every branch below is
 * assertable on a machine with no adapter.
 */

import type { SettingsStore } from "../audio/settings.ts";

export type RendererMode = "webgpu" | "webgl";

/**
 * Why a WebGPU boot did not happen. Every value except `no-webgpu` is a
 * *failure of a present implementation* and is therefore persisted; see the
 * module doc for why the absent-API case is not.
 */
export type RendererFallbackReason =
  /** `navigator.gpu` is undefined — no WebGPU implementation at all. Never persisted. */
  | "no-webgpu"
  /** `navigator.gpu.requestAdapter()` produced nothing (three throws its own message for this). */
  | "adapter"
  /** An adapter existed and `adapter.requestDevice()` rejected. */
  | "device"
  /** `renderer.init()` threw for some other reason — a backend that could not configure the canvas, say. */
  | "init"
  /** The `GPUDevice` was lost after a successful boot, and the reload came back here. */
  | "device-lost";

/** How this boot arrived at its backend — the word PREFERENCES and the boot report print. */
export type RendererModeSource =
  /** WebGPU, asked for and (so far as this decision knows) available. */
  | "preferred"
  /** `?renderer=webgl` — the capture hatch, pinned by hand. Not a statement about the driver. */
  | "pinned"
  /** WebGL, because a previous boot recorded a driver failure. */
  | "remembered"
  /** WebGL, because *this* boot's WebGPU attempt failed. */
  | "fallback";

/** The whole renderer story as one value: what is running and why. */
export interface RendererFacts {
  readonly mode: RendererMode;
  readonly source: RendererModeSource;
  /** Present whenever `source` is `"remembered"` or `"fallback"`. */
  readonly reason?: RendererFallbackReason;
}

/**
 * What was decided before any renderer was built, plus the two side effects the
 * caller owes the decision.
 */
export interface RendererPlan extends RendererFacts {
  /**
   * The stored record must be cleared — `?renderer=webgpu` was typed, and the
   * point of typing it is that the next boot re-probes rather than re-reads.
   */
  readonly clearsStoredFallback: boolean;
  /**
   * This plan is an *attempt*: if `init()` rejects, fall back to WebGL and
   * persist. False on every plan that is already WebGL, which is what stops the
   * capture hatch from writing a driver verdict it has no evidence for.
   */
  readonly mayFallBack: boolean;
}

/**
 * The persisted verdict, as a discriminated union rather than a nullable field.
 *
 * `SettingsStore` is `getItem`/`setItem` only — there is no `removeItem` on it
 * and adding one would widen an interface eleven other settings modules
 * implement — so "clear the flag" is *writing* `{ fallen: false }`. The union
 * makes that a state rather than a sentinel, and keeps `reason` from existing
 * on the record that has no reason.
 */
export type RendererFallbackRecord =
  | { readonly fallen: false }
  | { readonly fallen: true; readonly reason: RendererFallbackReason };

export const RENDERER_FALLBACK_STORAGE_KEY = "red-opus/renderer-fallback";

/** No driver failure on record — a fresh install, and what `?renderer=webgpu` writes back. */
export const NO_RENDERER_FALLBACK: RendererFallbackRecord = { fallen: false };

const PERSISTED_REASONS: readonly RendererFallbackReason[] = ["adapter", "device", "init", "device-lost"];

/**
 * Whether a reason is worth remembering across boots. `no-webgpu` is not — see
 * the module doc. This is also the read guard: a record naming `no-webgpu` can
 * only have come from a hand-edited or future-version blob, and is treated as
 * no record at all rather than as a reason to skip the probe.
 */
export function isPersistedFallbackReason(reason: RendererFallbackReason): boolean {
  return PERSISTED_REASONS.includes(reason);
}

export function normalizeRendererFallback(candidate: unknown): RendererFallbackRecord {
  if (typeof candidate !== "object" || candidate === null) return NO_RENDERER_FALLBACK;
  const raw = candidate as { fallen?: unknown; reason?: unknown };
  // `=== true` rather than `!== false`, because the default here is "no failure
  // on record" — the idiom `effects-settings.ts` documents for a flag whose
  // default is off, so a blob written before this key existed reads as absent.
  if (raw.fallen !== true) return NO_RENDERER_FALLBACK;
  const reason = raw.reason;
  if (typeof reason !== "string") return NO_RENDERER_FALLBACK;
  if (!isPersistedFallbackReason(reason as RendererFallbackReason)) return NO_RENDERER_FALLBACK;
  return { fallen: true, reason: reason as RendererFallbackReason };
}

export function serializeRendererFallback(record: RendererFallbackRecord): string {
  return JSON.stringify(record);
}

export function loadRendererFallback(store: SettingsStore | undefined): RendererFallbackRecord {
  if (!store) return NO_RENDERER_FALLBACK;
  const raw = store.getItem(RENDERER_FALLBACK_STORAGE_KEY);
  if (raw === null) return NO_RENDERER_FALLBACK;
  try {
    return normalizeRendererFallback(JSON.parse(raw));
  } catch {
    return NO_RENDERER_FALLBACK;
  }
}

export function saveRendererFallback(store: SettingsStore | undefined, record: RendererFallbackRecord): void {
  store?.setItem(RENDERER_FALLBACK_STORAGE_KEY, serializeRendererFallback(record));
}

/**
 * `?renderer=webgl` or `?renderer=webgpu`, or nothing.
 *
 * Both directions are pins and neither is a preference: the first is the
 * capture hatch `webgpu-support.ts` argues for at length, the second is the
 * escape hatch out of a remembered failure.
 */
export function rendererPin(search: string | undefined): RendererMode | undefined {
  if (!search) return undefined;
  const value = new URLSearchParams(search).get("renderer");
  return value === "webgl" || value === "webgpu" ? value : undefined;
}

export interface RendererDecisionInputs {
  /** `location.search`, or `undefined` where there is no location (tests, workers). */
  readonly search: string | undefined;
  /** What a previous boot recorded. */
  readonly stored: RendererFallbackRecord;
  /** `hasWebGpu()` — whether `navigator.gpu` exists at all. */
  readonly hasWebGpu: boolean;
}

/**
 * The boot-time decision, in the order the four inputs beat each other.
 *
 * 1. **`?renderer=webgl` wins outright.** It is the capture hatch, and a
 *    capture must land on the backend it asked for whatever the machine could
 *    have done. It does *not* clear the stored record: pinning WebGL is not a
 *    verdict about the driver, and clearing here would silently un-remember a
 *    real failure the moment anyone took a screenshot.
 * 2. **`?renderer=webgpu` clears the record and then falls through**, so that
 *    it re-probes rather than overriding a `navigator.gpu` that is genuinely
 *    absent — there is nothing to override there, and pretending otherwise
 *    would produce a renderer that cannot init and an error screen instead of
 *    a game.
 * 3. **No `navigator.gpu` → WebGL, unpersisted.**
 * 4. **A remembered driver failure → WebGL**, without paying for the probe again.
 * 5. Otherwise WebGPU, as an attempt that may still fall back.
 */
export function decideRendererMode(inputs: RendererDecisionInputs): RendererPlan {
  const pin = rendererPin(inputs.search);
  if (pin === "webgl") {
    return { mode: "webgl", source: "pinned", clearsStoredFallback: false, mayFallBack: false };
  }
  const clearsStoredFallback = pin === "webgpu";
  const remembered = clearsStoredFallback ? NO_RENDERER_FALLBACK : inputs.stored;
  if (!inputs.hasWebGpu) {
    return { mode: "webgl", source: "fallback", reason: "no-webgpu", clearsStoredFallback, mayFallBack: false };
  }
  if (remembered.fallen) {
    return { mode: "webgl", source: "remembered", reason: remembered.reason, clearsStoredFallback, mayFallBack: false };
  }
  return { mode: "webgpu", source: "preferred", clearsStoredFallback, mayFallBack: true };
}

/** Drops a plan's side-effect flags, leaving the two fields anything reporting the decision wants. */
export function factsOf(plan: RendererPlan): RendererFacts {
  return plan.reason === undefined
    ? { mode: plan.mode, source: plan.source }
    : { mode: plan.mode, source: plan.source, reason: plan.reason };
}

/**
 * Whether these facts describe a backend nobody asked for — WebGL reached
 * because WebGPU could not be, rather than because it was pinned.
 *
 * The one predicate the UI needs: `pinned` prints its backend without
 * apologising for it, everything else with a reason gets the footer that names
 * the escape hatch.
 */
export function isRendererFallback(facts: RendererFacts): boolean {
  return facts.source === "remembered" || facts.source === "fallback";
}

/** `RENDERER: WEBGPU` / `RENDERER: WEBGL` — the fact line, in the shell's own upper case. */
export function rendererFactLabel(facts: RendererFacts): string {
  return `RENDERER: ${facts.mode === "webgpu" ? "WEBGPU" : "WEBGL"}`;
}
